import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, Search, Loader2, Download, FileText, Wallet } from "lucide-react";
import api from "./api";
import { generateBoxInvoicePdf } from "../lib/invoicePdf";
import { useTuberAudit } from "./useTuberAudit";

const EMPTY_LINE = { name: "", qty: 1, unit_price: 0, variety_id: null, source: "custom" };

const DEFAULT_TERMS = `Heritage Dahlias NZ Ltd , standard terms.
Tubers are perishable goods. Once dispatched, all sales are final.
Quality concerns must be raised within 48 hours of delivery.
Payment via bank transfer , reference: customer name + invoice number.`;

const PAYMENT_METHODS = [
  { v: "bank-transfer", label: "Bank transfer" },
  { v: "card",          label: "Card (manual)" },
  { v: "cash",          label: "Cash" },
  { v: "credit",        label: "Heritage credit" },
];

const PAYMENT_STATUSES = [
  { v: "unpaid",  label: "Unpaid",   color: "bg-[#A33030] text-white" },
  { v: "partial", label: "Partial",  color: "bg-[#C49247] text-white" },
  { v: "paid",    label: "Paid",     color: "bg-[#2F4A2A] text-white" },
];

const DELIVERY_TYPES = [
  { v: "urban", label: "Urban delivery", price: 12 },
  { v: "rural", label: "Rural delivery", price: 18 },
  { v: "local", label: "Local delivery", price: 0 },
];

export default function ConsoleCreateOrder({ fromAudit = null }) {
  const auditQty = useTuberAudit();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const bankQueueId = search.get("bank_queue_id") || "";
  const bankAmount = Number(search.get("bank_amount") || 0);
  const bankName = search.get("bank_name") || "";
  const bankDate = search.get("bank_date") || "";

  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [pickTabs, setPickTabs] = useState({});   // line idx -> 'boxes' | 'tubers'
  const [destination, setDestination] = useState("active"); // 'active' | 'historical'
  const [completedOn, setCompletedOn] = useState("");       // for historical records
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [creatingNewCustomer, setCreatingNewCustomer] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(false);

  const [form, setForm] = useState(() => ({
    customer_id: search.get("customer_id") || "",
    customer_name: search.get("name") || (fromAudit?.customer_name || ""),
    email: search.get("email") || (fromAudit?.email || ""),
    phone: search.get("phone") || (fromAudit?.phone || ""),
    address: search.get("address") || (fromAudit?.address || ""),
    line_items: bankQueueId
      ? [{ ...EMPTY_LINE, name: bankName ? `Bank payment - ${bankName}` : "Bank payment", unit_price: Math.abs(bankAmount), source: "bank-audit" }]
      : fromAudit?.line_items?.length
      ? fromAudit.line_items
      : [{ ...EMPTY_LINE }],
    shipping_price: Number(fromAudit?.shipping_price || 0),
    shipping_provider: "NZ Post",
    delivery_type: "",
    notes: bankQueueId ? `Loaded from Bank Auditor Queue${bankDate ? ` (${bankDate})` : ""}. Please confirm the order items before saving.` : (fromAudit?.notes || ""),
    terms: DEFAULT_TERMS,
    payment_method: "bank-transfer",
    payment_status: bankQueueId ? "paid" : "unpaid",
    hq_picks_note: "",
    hq_pick_count: 0,
  }));

  /* When opened via "Reuse these picks" from the Historic Orders tab,
     pull the staged payload out of sessionStorage and prefill. */
  useEffect(() => {
    if (search.get("from_picks") !== "1") return;
    try {
      const raw = sessionStorage.getItem("heritage-reuse-picks");
      if (!raw) return;
      const stash = JSON.parse(raw);
      setForm((f) => ({
        ...f,
        customer_name: stash.customer_name || f.customer_name,
        email:         stash.email         || f.email,
        phone:         stash.phone         || f.phone,
        address:       stash.address       || f.address,
        hq_picks_note: stash.hq_picks_note || f.hq_picks_note,
        hq_pick_count: Number(stash.hq_pick_count) || f.hq_pick_count,
        notes: [f.notes, stash.notes].filter(Boolean).join("\n"),
      }));
      sessionStorage.removeItem("heritage-reuse-picks");
      toast.success("Customer + box picks prefilled. Add varieties and save.");
    } catch { /* soft */ }
  }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const [c, inv, prods] = await Promise.all([
          api.get("/console/customers"),
          api.get("/console/inventory"),
          api.get("/admin/products"),
        ]);
        setCustomers(c.data || []);
        setInventory(inv.data || []);
        setBoxes((prods.data || []).filter((p) => p.category === "boxes"));
      } catch { /* soft */ }
    })();
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 30);
    return customers
      .filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phone || "").toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [customers, customerSearch]);

  const subtotal = useMemo(
    () => form.line_items.reduce((s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0),
    [form.line_items],
  );
  const total = useMemo(
    () => subtotal + Number(form.shipping_price || 0),
    [subtotal, form.shipping_price],
  );

  const pickCustomer = (c) => {
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_name: c.name,
      email: c.email || f.email,
      phone: c.phone || f.phone,
      address: c.address || f.address,
    }));
    setCustomerSearch(c.name || c.email);
    setShowCustomerPicker(false);
    setCreatingNewCustomer(false);
  };

  const addLine = (preset = {}) => {
    setForm((f) => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE, ...preset }] }));
  };

  const updateLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    }));
  };

  const removeLine = (idx) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.filter((_, i) => i !== idx),
    }));
  };

  const loadFromInventory = (idx, varietyId) => {
    const v = inventory.find((x) => x.id === varietyId);
    if (!v) return;
    updateLine(idx, {
      variety_id: v.id,
      name: v.name,
      unit_price: Number(v.price || 0),
      source: "inventory",
    });
  };

  const loadFromBoxes = (idx, productId) => {
    const b = boxes.find((x) => x.id === productId);
    if (!b) return;
    updateLine(idx, {
      variety_id: null,
      name: b.name,
      unit_price: Number(b.base_price || 0),
      source: "box",
    });
  };

  const renderInvoice = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name required to render invoice."); return; }
    setSavingInvoice(true);
    try {
      const today = new Date();
      const due = new Date(today.getTime() + 7 * 86400 * 1000);
      const iso = (d) => d.toISOString().slice(0, 10);
      const invoiceNumber = `HD-${iso(today).replace(/-/g, "")}-${String(Math.floor(Math.random() * 900 + 100))}`;
      const items = form.line_items
        .filter((li) => li.name?.trim())
        .map((li) => ({
          description: li.name + (li.source === "inventory" ? " (heritage stock)" : ""),
          qty: Number(li.qty || 1),
          unit_price: Number(li.unit_price || 0),
        })).concat(form.shipping_price > 0 ? [{
          description: `Shipping (${form.shipping_provider || "NZ Post"})`,
          qty: 1,
          unit_price: Number(form.shipping_price || 0),
        }] : []);
      const result = generateBoxInvoicePdf({
        invoiceNumber,
        issueDate: iso(today),
        dueDate: iso(due),
        customer: { name: form.customer_name, email: form.email, phone: form.phone, address: form.address },
        items,
        subtotal: total,
        creditApplied: 0,
        total,
        notes: form.notes,
      });
      toast.success("Invoice downloaded. Tap Save Order to stamp it onto the customer record.");
      // Stash the rendered PDF so save() can upload it after the order is created.
      window.__heritage_pending_invoice = result;
    } catch {
      toast.error("Couldn't render invoice.");
    } finally { setSavingInvoice(false); }
  };

  const save = async () => {
    if (!form.customer_name.trim()) {
      toast.error("Customer name is required.");
      return;
    }
    setSavingInvoice(true);
    try {
      let customer_id = form.customer_id;
      if (creatingNewCustomer || !customer_id) {
        const c = await api.post("/console/customers", {
          name: form.customer_name,
          email: form.email,
          phone: form.phone,
          address: form.address,
        });
        customer_id = c.data.id;
      }
      const payload = {
        customer_id,
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        line_items: form.line_items
          .filter((li) => li.name?.trim())
          .map((li) => ({
            name: li.name,
            qty: Number(li.qty || 1),
            unit_price: Number(li.unit_price || 0),
            variety_id: li.variety_id,
            source: li.source || "custom",
          })),
        shipping_price: Number(form.shipping_price || 0),
        shipping_provider: form.shipping_provider,
        notes: form.notes,
        terms: form.terms,
        payment_method: form.payment_method,
        payment_status: form.payment_status,
        hq_picks_note: form.hq_picks_note,
        hq_pick_count: Number(form.hq_pick_count || 0),
      };
      let order;
      if (destination === "historical") {
        const total = payload.line_items.reduce((s, li) => s + li.qty * li.unit_price, 0) + Number(payload.shipping_price || 0);
        await api.post("/admin/historical-orders", {
          customer_name: payload.customer_name,
          email: payload.email,
          phone: payload.phone,
          address: payload.address,
          line_items: payload.line_items,
          shipping_price: payload.shipping_price,
          total,
          notes: payload.notes,
          completed_on: completedOn,
          mark_logged: true,
        });
        toast.success("Completed historical order logged , record only, no packing or payment chase.");
        navigate("/admin/orders/completed-historical");
        return;
      }
      if (fromAudit?.audit_id) {
        const r = await api.post(`/console/audit/${fromAudit.audit_id}/create-fresh`, {
          order: payload,
          customer: {
            name: form.customer_name, email: form.email,
            phone: form.phone, address: form.address,
          },
        });
        order = r.data;
        toast.success("Fresh order created. Legacy entry marked as audited.");
      } else {
        const r = await api.post("/console/orders", payload);
        order = r.data;
        const dest = order.stage === "assigned" ? "Packing" : "Awaiting Payment";
        toast.success(`Order ${order.order_number || order.id?.slice(0, 8)} saved , now in ${dest}.`);
        if (search.get("historic") && form.email?.trim()) {
          try { await api.post(`/admin/historic/by-email/${encodeURIComponent(form.email)}/mark-logged`); } catch { /* non-fatal */ }
        }
      }
      if (bankQueueId && order?.id) {
        await api.post(`/console/bank-audit/queue/${bankQueueId}/assign-order`, { order_id: order.id });
        toast.success("Bank payment loaded into the matched customer's orders.");
      }
      // If the admin rendered an invoice first, stamp it onto the order now.
      const pending = window.__heritage_pending_invoice;
      if (pending && pending.base64) {
        try {
          await api.post(`/console/orders/${order.id}/invoice`, {
            pdf_base64: pending.base64,
            filename: pending.filename,
            total: pending.total,
          });
          toast.success("Invoice PDF saved to customer record.");
        } catch { /* non-fatal */ }
        window.__heritage_pending_invoice = null;
      }
      // Email the invoice + pay link straight to the customer (no code, no copy-paste).
      if (emailInvoice && form.email?.trim()) {
        try {
          await api.post(`/console/orders/${order.id}/email-invoice`, { origin_url: window.location.origin });
          toast.success(`Invoice emailed to ${form.email}.`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Order saved, but the invoice email failed.");
        }
      }
      navigate(bankQueueId ? "/admin/bank-auditor-queue?status=loaded" : (order?.stage === "assigned" ? "/admin/orders/packing" : "/admin/orders/awaiting-payment"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSavingInvoice(false);
    }
  };

  return (
    <div data-testid="console-create-order" className="max-w-5xl">
      <header className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">
          Heritage Dahlias · Console
        </div>
        <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1">
          {fromAudit ? "Create Fresh Order , from Audit" : "Create Order"}
        </h1>
        {fromAudit && (
          <p className="text-[#7A6A55] mt-2 text-sm">
            Old order info is shown on the left for reference. Saving creates a
            brand new record and marks the legacy order as audited.
          </p>
        )}
        {bankQueueId && (
          <div className="mt-4 border-l-4 border-[#C49247] bg-[#FBF3DF] px-4 py-3 text-sm text-[#5B4224]" data-testid="bank-queue-order-banner">
            Matched bank payment: <strong>{bankName || "Bank transaction"}</strong> · ${Math.abs(bankAmount).toFixed(2)}{bankDate ? ` · ${bankDate}` : ""}. Confirm the customer and replace the placeholder item with the real order details before saving.
          </div>
        )}
      </header>

      <div className={fromAudit ? "grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" : ""}>
        <div className="space-y-6">
          {/* Customer */}
          <Card title="Customer">
            <div className="space-y-3">
              <div className="relative">
                <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E8DFCB] rounded">
                  <Search className="w-4 h-4 text-[#9C8B73]" />
                  <input
                    type="text"
                    placeholder="Search existing customer by name, email, phone…"
                    value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerPicker(true); }}
                    onFocus={() => setShowCustomerPicker(true)}
                    data-testid="console-customer-search"
                    className="flex-1 outline-none text-sm bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, customer_id: "", customer_name: "", email: "", phone: "", address: "" }));
                      setCreatingNewCustomer(true);
                      setShowCustomerPicker(false);
                      setCustomerSearch("");
                    }}
                    data-testid="console-customer-new"
                    className="text-[11px] tracking-[0.2em] uppercase text-[#6E1E3A] hover:underline whitespace-nowrap"
                  >
                    + New customer
                  </button>
                </div>
                {showCustomerPicker && customerSearch.trim() && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-[#E8DFCB] rounded shadow-lg max-h-72 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-[#7A6A55] italic">No matches.</div>
                    ) : filteredCustomers.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickCustomer(c)}
                        data-testid={`console-customer-pick-${c.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#F4ECD8] border-b border-[#F4ECD8] last:border-b-0"
                      >
                        <div className="text-sm text-[#3C2A1E] font-medium">{c.name || c.email}</div>
                        <div className="text-xs text-[#7A6A55]">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name">
                  <Text value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="customer-name" />
                </Field>
                <Field label="Phone">
                  <Text value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="customer-phone" />
                </Field>
                <Field label="Email">
                  <Text value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="customer-email" />
                </Field>
                <Field label="Delivery address">
                  <Text value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="customer-address" />
                </Field>
              </div>
              {creatingNewCustomer && (
                <div className="text-xs text-[#6E1E3A] flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> New customer profile will be saved when you save the order.
                </div>
              )}
            </div>
          </Card>

          {/* Line items */}
          <Card title="Order items" subtitle="Pick a variety from inventory, or add a custom line.">
            <div className="space-y-2">
              {form.line_items.map((li, idx) => {
                const isInv = li.source === "inventory" && li.variety_id;
                const isBox = li.source === "box" && li.name;
                const inv = isInv ? inventory.find((x) => x.id === li.variety_id) : null;
                const tab = pickTabs[idx] || "boxes";
                return (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-[#FBF6E9] border border-[#E8DFCB] rounded p-2.5" data-testid={`line-${idx}`}>
                  <div className="col-span-12 md:col-span-6">
                    {isInv || isBox ? (
                      <div className="flex items-center justify-between gap-2 px-2.5 py-2 bg-white border border-[#6E1E3A]/30 rounded">
                        <div className="min-w-0">
                          <div className="text-sm font-serif text-[#3C2A1E] truncate">{li.name}</div>
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#6E1E3A]">
                            {isBox ? "Heritage box" : `Heritage stock · ${auditQty(li.name) !== null ? `${auditQty(li.name)} in tuber audit` : "not in tuber audit"}`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateLine(idx, { variety_id: null, source: "custom", name: "" })}
                          data-testid={`line-clear-${idx}`}
                          className="text-[10px] tracking-[0.2em] uppercase text-[#A33030] hover:underline"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex gap-1 mb-1.5" data-testid={`line-pick-tabs-${idx}`}>
                          {[{ v: "boxes", l: "Boxes" }, { v: "tubers", l: "Tubers" }].map((t) => (
                            <button
                              key={t.v}
                              type="button"
                              onClick={() => setPickTabs((p) => ({ ...p, [idx]: t.v }))}
                              data-testid={`line-tab-${t.v}-${idx}`}
                              className={`px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-semibold rounded ${
                                tab === t.v ? "bg-[#6E1E3A] text-white" : "bg-white border border-[#E8DFCB] text-[#7A6A55] hover:border-[#6E1E3A]/40"
                              }`}
                            >
                              {t.l}
                            </button>
                          ))}
                        </div>
                        {tab === "boxes" ? (
                          <select
                            value=""
                            onChange={(e) => { if (e.target.value) loadFromBoxes(idx, e.target.value); }}
                            data-testid={`line-boxes-${idx}`}
                            className="w-full mb-1.5 px-2 py-1.5 text-xs bg-white border border-[#E8DFCB] rounded"
                          >
                            <option value="">{boxes.length ? "Pick a box…" : "No boxes in the store yet"}</option>
                            {boxes.map((b) => (
                              <option key={b.id} value={b.id}>{`${b.name} · $${Number(b.base_price || 0).toFixed(2)}`}</option>
                            ))}
                          </select>
                        ) : (
                          <select
                            value=""
                            onChange={(e) => { if (e.target.value) loadFromInventory(idx, e.target.value); }}
                            data-testid={`line-inventory-${idx}`}
                            className="w-full mb-1.5 px-2 py-1.5 text-xs bg-white border border-[#E8DFCB] rounded"
                          >
                            <option value="">Pick a tuber…</option>
                            {inventory.map((v) => {
                              const audit = auditQty(v.name);
                              const stockSuffix = audit !== null ? ` (${audit} in audit)` : " (not audited)";
                              const priceSuffix = ` · $${Number(v.price || 0).toFixed(2)}`;
                              const label = `${v.name}${stockSuffix}${priceSuffix}`;
                              return <option key={v.id} value={v.id}>{label}</option>;
                            })}
                          </select>
                        )}
                        <Text
                          value={li.name}
                          onChange={(v) => updateLine(idx, { name: v })}
                          placeholder="…or type a custom item name"
                          testid={`line-name-${idx}`}
                        />
                        {auditQty(li.name) !== null && (
                          <div className="text-[10px] font-semibold text-[#2F6B4F] mt-0.5" data-testid={`line-audit-count-${idx}`}>
                            Tuber Audit: {auditQty(li.name)} counted
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Field label="Qty" tight>
                      <NumberInput value={li.qty} onChange={(v) => updateLine(idx, { qty: v })} testid={`line-qty-${idx}`} />
                    </Field>
                  </div>
                  <div className="col-span-5 md:col-span-2">
                    <Field label="Unit $" tight>
                      <NumberInput step="0.01" value={li.unit_price} onChange={(v) => updateLine(idx, { unit_price: v })} testid={`line-price-${idx}`} />
                    </Field>
                  </div>
                  <div className="col-span-3 md:col-span-1 text-right text-sm text-[#3C2A1E] pb-1.5">
                    ${(Number(li.qty || 0) * Number(li.unit_price || 0)).toFixed(2)}
                  </div>
                  <div className="col-span-1 pb-1.5 text-right">
                    <button type="button" onClick={() => removeLine(idx)} data-testid={`line-remove-${idx}`} className="text-[#A33030] hover:bg-[#A33030]/10 p-1.5 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                );
              })}
              <button
                type="button"
                onClick={() => addLine()}
                data-testid="line-add"
                className="text-sm text-[#6E1E3A] hover:underline inline-flex items-center gap-1 mt-2"
              >
                <Plus className="w-3.5 h-3.5" /> Add another line
              </button>
            </div>
          </Card>

          {/* HQ catalogue pick allowance */}
          <Card title="HQ catalogue picks" subtitle="How many tubers can this customer pick from the Heritage HQ catalogue for this order?">
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3 items-center">
              <Field label="Tubers allowed" tight>
                <NumberInput value={form.hq_pick_count} onChange={(v) => setForm({ ...form, hq_pick_count: Math.max(0, Math.floor(v || 0)) })} testid="hq-pick-count" />
              </Field>
              <div className="text-xs text-[#7A6A55]">
                Customer will see a &ldquo;Pick from catalogue&rdquo; panel inside HQ on this order. Leave at <strong>0</strong> if there are no HQ picks.
              </div>
            </div>
            <Field label="Picks note (loves, avoids, special requests, variety list)" tight>
              <textarea
                value={form.hq_picks_note}
                onChange={(e) => setForm({ ...form, hq_picks_note: e.target.value })}
                data-testid="hq-picks-note"
                rows={5}
                placeholder="Loves: …&#10;Avoid: …&#10;Special requests: …&#10;Varieties chosen: …"
                className="w-full px-3 py-2 text-sm bg-white border border-[#E8DFCB] rounded font-serif leading-relaxed"
              />
            </Field>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Shipping">
              <div className="space-y-3">
                <Field label="Delivery type">
                  <div className="flex gap-2">
                    {DELIVERY_TYPES.map((d) => (
                      <button
                        key={d.v}
                        type="button"
                        onClick={() => setForm({ ...form, delivery_type: d.v, shipping_price: d.price })}
                        data-testid={`delivery-type-${d.v}`}
                        className={`flex-1 py-1.5 px-1 text-[10px] tracking-[0.15em] uppercase font-semibold rounded ${
                          form.delivery_type === d.v
                            ? "bg-[#6E1E3A] text-white"
                            : "bg-white border border-[#E8DFCB] text-[#7A6A55] hover:border-[#6E1E3A]/40"
                        }`}
                      >
                        {d.label.replace(" delivery", "")}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-[10px] normal-case tracking-normal font-normal text-[#9C8B73]">
                    Urban $12 · Rural $18 · Local free , price prefills below, adjust as needed.
                  </p>
                </Field>
                <Field label="Provider">
                  <select
                    value={form.shipping_provider}
                    onChange={(e) => setForm({ ...form, shipping_provider: e.target.value })}
                    data-testid="shipping-provider"
                    className="w-full px-3 py-2 text-sm bg-white border border-[#E8DFCB] rounded"
                  >
                    <option>NZ Post</option>
                  </select>
                </Field>
                <Field label="Shipping price NZ$">
                  <NumberInput step="0.01" value={form.shipping_price} onChange={(v) => setForm({ ...form, shipping_price: v })} testid="shipping-price" />
                </Field>
              </div>
            </Card>
            <Card title="Payment">
              <div className="space-y-3">
                <Field label="Method">
                  <select
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    data-testid="payment-method"
                    className="w-full px-3 py-2 text-sm bg-white border border-[#E8DFCB] rounded"
                  >
                    {PAYMENT_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <div className="flex gap-2">
                    {PAYMENT_STATUSES.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setForm({ ...form, payment_status: s.v })}
                        data-testid={`payment-status-${s.v}`}
                        className={`flex-1 py-1.5 text-[11px] tracking-[0.2em] uppercase rounded ${form.payment_status === s.v ? s.color : "bg-white border border-[#E8DFCB] text-[#7A6A55]"}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </Card>
          </div>

          <Card title="Notes (visible on invoice & customer record)">
            <TextArea rows={3} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="order-notes" />
          </Card>

          <Card title="Terms & conditions">
            <TextArea rows={4} value={form.terms} onChange={(v) => setForm({ ...form, terms: v })} testid="order-terms" />
          </Card>

          {/* Totals + actions */}
          <Card title="Total" tone="berry">
            <div className="flex justify-between text-sm text-white/85">
              <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-white/85 mt-1">
              <span>Shipping</span><span>${Number(form.shipping_price || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-serif text-2xl text-white mt-3 pt-3 border-t border-white/20">
              <span>NZ$</span><span data-testid="order-total">${total.toFixed(2)}</span>
            </div>
            <div className="mt-4">
              <label className="inline-flex items-center gap-2 text-xs text-white/90 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailInvoice}
                  onChange={(e) => setEmailInvoice(e.target.checked)}
                  data-testid="email-invoice-toggle"
                  className="w-4 h-4 accent-white"
                />
                Email invoice + payment link to customer
              </label>
            </div>
            <div className="mt-5 pt-4 border-t border-white/25">
              <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-2 opacity-90">Where does this order go?</div>
              <div className="flex flex-col gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="destination-active">
                  <input type="radio" name="destination" checked={destination === "active"} onChange={() => setDestination("active")} />
                  <span><strong>Send to All Orders</strong> , normal active order (assign, pack, ship, live financials)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="destination-historical">
                  <input type="radio" name="destination" checked={destination === "historical"} onChange={() => setDestination("historical")} />
                  <span><strong>Send to Completed Historical Orders</strong> , record only, already fulfilled, kept out of live reporting</span>
                </label>
                {destination === "historical" && (
                  <label className="flex items-center gap-2 mt-1">
                    <span className="text-xs opacity-90">Completed on:</span>
                    <input type="date" value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} className="h-8 px-2 rounded text-[#3C2A1E]" data-testid="historical-completed-on" />
                  </label>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={renderInvoice}
                data-testid="render-invoice"
                className="px-5 py-2.5 bg-white text-[#6E1E3A] hover:bg-[#F4ECD8] rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-2"
              >
                <Download className="w-3.5 h-3.5" /> Render invoice PDF
              </button>
              <button
                type="button"
                onClick={save}
                disabled={savingInvoice}
                data-testid="save-order"
                className="px-5 py-2.5 bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {savingInvoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {savingInvoice ? "Saving…" : "Save Order"}
              </button>
            </div>
          </Card>
        </div>

        {/* Audit reference column */}
        {fromAudit && (
          <aside className="bg-white border border-[#E8DFCB] rounded-md p-4 h-fit sticky top-6">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold">
              Legacy order , reference
            </div>
            <div className="font-serif text-xl text-[#3C2A1E] mt-1">
              {fromAudit.customer_name || "Unknown"}
            </div>
            <div className="text-xs text-[#7A6A55] mt-0.5">{fromAudit.email}</div>
            {fromAudit.webador_order_no && (
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] mt-1">
                Webador #{fromAudit.webador_order_no}
              </div>
            )}
            <hr className="my-3 border-[#E8DFCB]" />
            {fromAudit.items && (
              <div className="mb-3">
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#9C8B73] mb-1">Items</div>
                <pre className="whitespace-pre-wrap text-xs text-[#3C2A1E] font-sans">{fromAudit.items}</pre>
              </div>
            )}
            {fromAudit.notes && (
              <div className="mb-3">
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#9C8B73] mb-1">Notes</div>
                <pre className="whitespace-pre-wrap text-xs italic text-[#3C2A1E] font-sans">{fromAudit.notes}</pre>
              </div>
            )}
            {fromAudit.amount !== undefined && (
              <div className="text-xs text-[#3C2A1E]">
                <div>Original amount , <strong>${Number(fromAudit.amount || 0).toFixed(2)}</strong></div>
                <div>Paid , <strong>{fromAudit.paid ? "yes" : "no"}</strong></div>
              </div>
            )}
            {fromAudit.address && (
              <div className="mt-3">
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#9C8B73] mb-1">Address</div>
                <div className="text-xs text-[#3C2A1E] whitespace-pre-wrap">{fromAudit.address}</div>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

// ---------- shared atoms ----------
export function Card({ title, subtitle, children, tone = "default" }) {
  const cls = tone === "berry"
    ? "bg-[#6E1E3A] text-white"
    : "bg-white border border-[#E8DFCB]";
  return (
    <section className={`${cls} rounded-md p-5`}>
      {title && (
        <header className="mb-3">
          <h2 className={`font-serif text-lg ${tone === "berry" ? "text-white" : "text-[#3C2A1E]"}`}>{title}</h2>
          {subtitle && <p className={`text-xs mt-0.5 ${tone === "berry" ? "text-white/75" : "text-[#9C8B73]"}`}>{subtitle}</p>}
        </header>
      )}
      <div>{children}</div>
    </section>
  );
}

export function Field({ label, children, tight = false }) {
  return (
    <label className={`block ${tight ? "text-[10px]" : "text-[11px]"} tracking-[0.25em] uppercase text-[#9C8B73] font-semibold`}>
      {label}
      <div className="mt-1.5 font-normal normal-case tracking-normal text-sm">{children}</div>
    </label>
  );
}

export function Text({ value, onChange, placeholder = "", testid = "" }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid || undefined}
      className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] focus:outline-none focus:border-[#6E1E3A]"
    />
  );
}

export function NumberInput({ value, onChange, step = "1", testid = "" }) {
  const display = Number.isFinite(value) ? value : "";
  return (
    <input
      type="number"
      value={display}
      step={step}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        onChange(Number.isFinite(v) ? v : 0);
      }}
      data-testid={testid || undefined}
      className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] focus:outline-none focus:border-[#6E1E3A]"
    />
  );
}

export function TextArea({ rows = 3, value, onChange, placeholder = "", testid = "" }) {
  return (
    <textarea
      rows={rows}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid || undefined}
      className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] leading-relaxed focus:outline-none focus:border-[#6E1E3A]"
    />
  );
}
